import React, { useState, useEffect, useCallback } from 'react';

const API = '/api';
function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
}

const C = {
  grn: '#3B6D11', grnBg: '#EAF3DE',
  amb: '#854F0B', ambBg: '#FAEEDA',
  red: '#A32D2D', redBg: '#FCEBEB',
  acc: '#185FA5', accBg: '#E6F1FB',
  border: 'rgba(0,0,0,0.08)', bg: '#F2F0EB', card: '#fff',
  txt: '#1a1a1a', txt2: '#666'
};

function heatColor(mins) {
  if (!mins) return { bg: '#F0EDE8', color: C.txt2 };
  if (mins > 50) return { bg: '#7F1D1D', color: '#fff' };
  if (mins > 35) return { bg: '#DC2626', color: '#fff' };
  if (mins > 20) return { bg: '#F97316', color: '#fff' };
  return { bg: '#FCD34D', color: '#92400E' };
}
function statusColor(status) {
  const s = (status || '').toLowerCase();
  if (s === 'running') return { bg: C.redBg, color: C.red, label: 'Running' };
  if (s === 'attended') return { bg: C.ambBg, color: C.amb, label: 'Attended' };
  return { bg: C.grnBg, color: C.grn, label: 'Resolved' };
}
function KpiTile({ label, value, sub, color }) {
  return (
    <div style={{ background: C.card, borderRadius: 10, padding: '12px 16px', border: `0.5px solid ${C.border}`, flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || C.txt, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.txt2, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function SimpleTable({ title, rows, cols }) {
  if (!rows.length) return <div style={{ padding: 16, color: C.txt2, fontSize: 12 }}>No data</div>;
  return (
    <div style={{ background: C.card, borderRadius: 10, padding: '14px 16px', border: `0.5px solid ${C.border}` }}>
      {title && <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>{title}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#F8F7F4' }}>
            {cols.map(c => <th key={c.key} style={{ padding: '6px 8px', textAlign: c.align || 'left', fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: 'uppercase' }}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAF8', borderBottom: `0.5px solid ${C.border}` }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '6px 8px', textAlign: c.align || 'left', fontWeight: c.bold ? 700 : 400, color: c.color ? c.color(row) : C.txt }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTab({ data }) {
  const maxReason = Math.max(...(data.by_reason || []).map(r => r.count), 1);
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ flex: 1, background: C.card, borderRadius: 10, padding: '14px 16px', border: `0.5px solid ${C.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Top Downtime Reasons</div>
        {!(data.by_reason || []).length && <div style={{ color: C.txt2, fontSize: 12 }}>No events</div>}
        {(data.by_reason || []).map(r => (
          <div key={r.reason} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{r.reason}</span>
              <span style={{ fontSize: 11, color: C.txt2 }}>{r.count} events · {r.minutes} min</span>
            </div>
            <div style={{ background: '#E2E8F0', borderRadius: 4, height: 8 }}>
              <div style={{ width: `${(r.count / maxReason) * 100}%`, height: '100%', background: C.red, borderRadius: 4 }} />
            </div>
            <div style={{ fontSize: 10, color: C.txt2, marginTop: 2 }}>{r.lines_affected} line{r.lines_affected !== 1 ? 's' : ''} · {r.active} active</div>
          </div>
        ))}
      </div>
      <SimpleTable title="By Line" rows={data.by_line || []} cols={[
        { key: 'line_number', label: 'Line', bold: true, render: r => `Line ${r.line_number}` },
        { key: 'style_number', label: 'Style', color: () => C.txt2 },
        { key: 'count', label: 'Events', align: 'center' },
        { key: 'minutes', label: 'Minutes', align: 'center' },
        { key: 'active', label: 'Active', align: 'center', color: r => r.active > 0 ? C.red : C.grn, bold: true },
        { key: 'resolved', label: 'Resolved', align: 'center', color: () => C.grn },
      ]} />
    </div>
  );
}

function LineWiseTab({ data }) {
  return (
    <SimpleTable title="Line-wise Downtime" rows={data.by_line || []} cols={[
      { key: 'line_number', label: 'Line', bold: true, render: r => `Line ${r.line_number}` },
      { key: 'style_number', label: 'Style', color: () => C.txt2 },
      { key: 'count', label: 'Events', align: 'center' },
      { key: 'minutes', label: 'Total mins', align: 'center', bold: true },
      { key: 'active', label: 'Active', align: 'center', color: r => r.active > 0 ? C.red : C.grn, bold: true },
      { key: 'resolved', label: 'Resolved', align: 'center', color: () => C.grn },
    ]} />
  );
}

function MachineWiseTab({ data }) {
  return (
    <SimpleTable title="Machine-wise Downtime" rows={data.by_machine || []} cols={[
      { key: 'machine', label: 'Machine type', bold: true },
      { key: 'count', label: 'Events', align: 'center' },
      { key: 'minutes', label: 'Total mins', align: 'center', bold: true, color: r => r.minutes > 60 ? C.red : C.txt },
      { key: 'ops_affected', label: 'Operations', align: 'center', color: () => C.txt2 },
    ]} />
  );
}

function OperationWiseTab({ data }) {
  return (
    <SimpleTable title="Operation-wise Downtime" rows={data.by_operation || []} cols={[
      { key: 'operation', label: 'Operation', bold: true },
      { key: 'machine_type', label: 'Machine', color: () => C.txt2 },
      { key: 'count', label: 'Events', align: 'center' },
      { key: 'minutes', label: 'Total mins', align: 'center', bold: true, color: r => r.minutes > 30 ? C.red : C.txt },
      { key: 'active', label: 'Active', align: 'center', color: r => r.active > 0 ? C.red : C.grn },
    ]} />
  );
}

function HeatmapTab({ data }) {
  const hd = data.heatmap_data || [];
  const lines = [...new Set(hd.map(h => h.line_number))].sort();
  const reasons = [...new Set(hd.map(h => h.reason))].sort();
  const getVal = (line, reason) => hd.find(h => h.line_number === line && h.reason === reason);
  if (!hd.length) return <div style={{ padding: 20, color: C.txt2, fontSize: 13 }}>No downtime data.</div>;
  return (
    <div style={{ background: C.card, borderRadius: 10, padding: '14px 16px', border: `0.5px solid ${C.border}`, overflowX: 'auto' }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Line × Reason Heatmap (minutes)</div>
      <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            <th style={{ padding: '6px 12px', background: '#F8F7F4', fontSize: 10, fontWeight: 700, color: C.txt2 }}>Line / Reason</th>
            {reasons.map(r => <th key={r} style={{ padding: '6px 10px', background: '#F8F7F4', fontSize: 10, fontWeight: 700, color: C.txt2, whiteSpace: 'nowrap' }}>{r}</th>)}
          </tr>
        </thead>
        <tbody>
          {lines.map(line => (
            <tr key={line}>
              <td style={{ padding: '6px 12px', fontWeight: 700, background: '#F8F7F4', whiteSpace: 'nowrap' }}>Line {line}</td>
              {reasons.map(reason => {
                const val = getVal(line, reason);
                const { bg, color } = heatColor(val?.minutes);
                return <td key={reason} style={{ padding: '6px 10px', background: bg, color, textAlign: 'center', fontWeight: val ? 700 : 400 }}>{val ? `${val.minutes}m` : '—'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: C.txt2 }}>Minutes:</span>
        {[{ label: '1–20', bg: '#FCD34D', color: '#92400E' }, { label: '21–35', bg: '#F97316', color: '#fff' }, { label: '36–50', bg: '#DC2626', color: '#fff' }, { label: '>50', bg: '#7F1D1D', color: '#fff' }].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, background: l.bg }} />
            <span style={{ fontSize: 10, color: C.txt2 }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FullLogTab({ events }) {
  if (!events.length) return <div style={{ padding: 20, color: C.txt2, fontSize: 13 }}>No events.</div>;
  return (
    <div style={{ background: C.card, borderRadius: 10, border: `0.5px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr style={{ background: '#F8F7F4' }}>
              {['Line', 'Operation', 'Machine', 'Operator', 'Reason', 'Hour', 'Started', 'Duration', 'Status'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => {
              const sc = statusColor(e.status);
              const started = new Date(e.started_at);
              return (
                <tr key={e.id} style={{ background: (e.status||'').toLowerCase() !== 'resolved' ? '#FFF5F5' : i % 2 === 0 ? '#fff' : '#FAFAF8', borderBottom: `0.5px solid ${C.border}` }}>
                  <td style={{ padding: '7px 10px', fontWeight: 700 }}>Line {e.line_number}</td>
                  <td style={{ padding: '7px 10px' }}>{e.operation_name || '—'}</td>
                  <td style={{ padding: '7px 10px', color: C.txt2 }}>{e.machine_type || '—'}</td>
                  <td style={{ padding: '7px 10px', color: C.txt2 }}>{e.emp_name || '—'}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{e.reason_name || '—'}</td>
                  <td style={{ padding: '7px 10px', color: C.txt2, textAlign: 'center' }}>H{e.hour_number}</td>
                  <td style={{ padding: '7px 10px', color: C.txt2, whiteSpace: 'nowrap' }}>
                    {started.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </td>
                  <td style={{ padding: '7px 10px', fontWeight: 600, color: e.duration_mins_calc > 30 ? C.red : C.txt }}>
                    {Math.round(parseFloat(e.duration_mins_calc || 0))} min
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'linewise', label: 'Line-wise' },
  { id: 'machinewise', label: 'Machine-wise' },
  { id: 'operationwise', label: 'Operation-wise' },
  { id: 'heatmap', label: 'Heatmap' },
  { id: 'log', label: 'Full Log' },
];

export default function DowntimeDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [dateRange, setDateRange] = useState('today');
  const [depFilter, setDepFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [factoryFilter, setFactoryFilter] = useState('');

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Downtime dashboard';
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ date_range: dateRange });
    if (depFilter) params.append('deployment_id', depFilter);
    else if (factoryFilter) params.append('factory_id', factoryFilter);
    else if (divisionFilter) params.append('division_id', divisionFilter);
    fetch(`${API}/downtime-dashboard?${params}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (!d.error) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateRange, depFilter, factoryFilter, divisionFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Reset lower filters when higher-level filter changes
  const handleDivisionChange = (val) => { setDivisionFilter(val); setFactoryFilter(''); setDepFilter(''); };
  const handleFactoryChange = (val) => { setFactoryFilter(val); setDepFilter(''); };

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const s = data?.summary;

  const filteredFactories = divisionFilter
    ? (data?.factories || []).filter(f => String(f.division_id) === String(divisionFilter))
    : (data?.factories || []);

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.acc }}>Skillflo</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>Downtime Dashboard</div>
          <div style={{ fontSize: 12, color: C.txt2 }}>{dateStr} · {timeStr}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, fontSize: 12, background: C.card }}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>

          {/* Division */}
          <select value={divisionFilter} onChange={e => handleDivisionChange(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, fontSize: 12, background: C.card }}>
            <option value="">All divisions</option>
            {(data?.divisions || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>

          {/* Factory */}
          <select value={factoryFilter} onChange={e => handleFactoryChange(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, fontSize: 12, background: C.card }}>
            <option value="">All factories</option>
            {filteredFactories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>

          {/* Line */}
          <select value={depFilter} onChange={e => setDepFilter(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, fontSize: 12, background: C.card }}>
            <option value="">All lines</option>
            {(data?.deployments || []).map(d => <option key={d.id} value={d.id}>Line {d.line_number} · {d.style_number}</option>)}
          </select>

          <button onClick={loadData} style={{ padding: '5px 12px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 11, cursor: 'pointer', color: C.acc, fontWeight: 600 }}>↺ Refresh</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 13 }}>Loading…</div>}

      {!loading && s && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <KpiTile label="Total events" value={s.total_events} color={s.total_events > 0 ? C.red : C.grn} />
            <KpiTile label="Active now" value={s.active_count} color={s.active_count > 0 ? C.red : C.grn} sub={`${s.lines_affected} line${s.lines_affected !== 1 ? 's' : ''} affected`} />
            <KpiTile label="Total minutes lost" value={`${s.total_minutes}m`} sub={`${Math.floor(s.total_minutes/60)}h ${s.total_minutes%60}m`} color={C.red} />
            <KpiTile label="Avg resolution" value={`${s.avg_resolution_mins}m`} sub="Target < 15 min" color={s.avg_resolution_mins > 15 ? C.red : C.grn} />
            <KpiTile label="Resolved" value={s.resolved_count} sub={`${s.resolution_rate}% rate`} color={C.grn} />
          </div>

          <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: activeTab === t.id ? 700 : 500,
                background: activeTab === t.id ? C.acc : C.card,
                color: activeTab === t.id ? '#fff' : C.txt2
              }}>{t.label}</button>
            ))}
          </div>

          {activeTab === 'summary' && <SummaryTab data={data} />}
          {activeTab === 'linewise' && <LineWiseTab data={data} />}
          {activeTab === 'machinewise' && <MachineWiseTab data={data} />}
          {activeTab === 'operationwise' && <OperationWiseTab data={data} />}
          {activeTab === 'heatmap' && <HeatmapTab data={data} />}
          {activeTab === 'log' && <FullLogTab events={data.events || []} />}
        </>
      )}

      {!loading && !s && (
        <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 14 }}>No downtime data for selected period.</div>
      )}
    </div>
  );
}